import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Globe, Clock, ChevronRight, AlertCircle, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import PageWrapper from '../components/PageWrapper';
import Breadcrumbs from '../components/Breadcrumbs';
import MedicalText from '../components/MedicalText';
import { urologicCancerArticles } from '../data/urologicCancerContent';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cancer Articles Index Page
export const CancerIndex = () => {
  return (
    <PageWrapper>
      <Breadcrumbs items={[
        { label: 'Education Library', path: '/education' },
        { label: 'Urologic Cancers', path: '/cancer' }
      ]} />

      <div className="page-header-centered" data-testid="cancer-index">
        <h1>Urologic Cancers</h1>
        <p>Comprehensive patient education about cancers affecting the urinary and reproductive systems</p>
      </div>

      <div className="cancer-grid" data-testid="cancer-grid">
        {urologicCancerArticles.map(article => (
          <Link 
            to={`/cancer/${article.id}`}
            key={article.id}
            className="cancer-card"
            data-testid={`cancer-card-${article.id}`}
          >
            <div className="cancer-card-icon">🎗️</div>
            <h3>{article.title.en}</h3>
            <p>{article.content.en.substring(0, 100).trim()}...</p>
            <span className="cancer-card-link">
              Learn more <ChevronRight size={16} />
            </span>
          </Link>
        ))}
      </div>
    </PageWrapper>
  );
};

// Individual Cancer Article Page
const CancerArticle = () => {
  const { cancerId } = useParams();
  const [language, setLanguage] = useState('en');

  // Scroll to top when article loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [cancerId]);
  const [relatedVideos, setRelatedVideos] = useState([]);
  
  const article = urologicCancerArticles.find(a => a.id === cancerId);

  useEffect(() => {
    const loadRelatedVideos = async () => {
      if (!article?.tags) return;
      try {
        const tagsParam = article.tags.slice(0, 3).join(',');
        const response = await axios.get(`${API}/videos/related/${tagsParam}?limit=3`);
        setRelatedVideos(response.data || []);
      } catch (error) {
        console.error('Error loading related videos:', error);
      }
    };
    loadRelatedVideos();
  }, [article]);

  if (!article) {
    return (
      <PageWrapper>
        <div className="empty-state">
          <h2>Article Not Found</h2>
          <p>The cancer information you're looking for doesn't exist.</p>
          <Link to="/cancer" className="btn-primary">
            View All Cancer Information
          </Link>
        </div>
      </PageWrapper>
    );
  }

  const toggleLanguage = () => {
    setLanguage(lang => lang === 'en' ? 'es' : 'en');
  };

  // Calculate reading time
  const wordCount = article.content.en.split(' ').length;
  const readingTime = Math.ceil(wordCount / 200);

  // Parse inline bold text (converts **text** to <strong>text</strong>)
  const parseBoldText = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={idx}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  // Parse markdown-style bold text and headers
  const renderContent = (content) => {
    const lines = content.split('\n');
    const elements = [];
    let currentParagraph = [];

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      
      if (trimmedLine === '') {
        if (currentParagraph.length > 0) {
          elements.push(
            <p key={`p-${index}`}>
              <MedicalText>{currentParagraph.join(' ')}</MedicalText>
            </p>
          );
          currentParagraph = [];
        }
      } else if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**') && !trimmedLine.slice(2, -2).includes('**')) {
        // Bold header line (entire line is bold)
        if (currentParagraph.length > 0) {
          elements.push(
            <p key={`p-${index}`}>
              <MedicalText>{currentParagraph.join(' ')}</MedicalText>
            </p>
          );
          currentParagraph = [];
        }
        const headerText = trimmedLine.replace(/\*\*/g, '');
        elements.push(
          <h3 key={`h-${index}`} className="article-section-header">
            {headerText}
          </h3>
        );
      } else if (trimmedLine.startsWith('• ')) {
        // List item
        if (currentParagraph.length > 0) {
          elements.push(
            <p key={`p-${index}`}>
              <MedicalText>{currentParagraph.join(' ')}</MedicalText>
            </p>
          );
          currentParagraph = [];
        }
        const listText = trimmedLine.substring(2);
        elements.push(
          <div key={`li-${index}`} className="article-list-item">
            <span className="list-bullet">•</span>
            <span>{parseBoldText(listText)}</span>
          </div>
        );
      } else if (trimmedLine.startsWith('- ')) {
        // Sub-list item
        const subListText = trimmedLine.substring(2);
        elements.push(
          <div key={`sli-${index}`} className="article-sublist-item">
            <span className="sublist-bullet">–</span>
            <span>{parseBoldText(subListText)}</span>
          </div>
        );
      } else {
        currentParagraph.push(trimmedLine);
      }
    });

    // Don't forget remaining paragraph
    if (currentParagraph.length > 0) {
      elements.push(
        <p key={`p-final`}>
          <MedicalText>{currentParagraph.join(' ')}</MedicalText>
        </p>
      );
    }

    return elements;
  };

  // Topic path mapping
  const topicPaths = {
    'prostate': { path: '/anatomy/prostate', title: 'Prostate Anatomy' },
    'elevated-psa': { path: '/after-visit/elevated-psa', title: 'PSA Monitoring' },
    'prostate-biopsy': { path: '/after-visit/prostate-biopsy', title: 'Prostate Biopsy Aftercare' },
    'bladder': { path: '/anatomy/bladder', title: 'Bladder Anatomy' },
    'hematuria': { path: '/after-visit/hematuria', title: 'Hematuria Evaluation' },
    'cystoscopy': { path: '/after-visit/cystoscopy', title: 'Cystoscopy Aftercare' },
    'bcg-therapy': { path: '/education/bcg-therapy', title: 'BCG Therapy Guide' },
    'kidney': { path: '/anatomy/kidney', title: 'Kidney Anatomy' },
    'renal-cysts': { path: '/learn/renal-cysts', title: 'Kidney Cysts & Masses' },
    'testicle': { path: '/anatomy/testicle', title: 'Testicle Anatomy' },
    'testicular-pain': { path: '/after-visit/testicular-pain', title: 'Testicular Pain' },
    'scrotal-cysts': { path: '/common-concerns/scrotal-cysts', title: 'Scrotal Cysts' },
    'ureter': { path: '/anatomy/ureter', title: 'Ureter Anatomy' },
    'bladder-cancer': { path: '/cancer/bladder-cancer', title: 'Bladder Cancer' },
    'urethra': { path: '/anatomy/urethra', title: 'Urethra Anatomy' },
    'genital-warts': { path: '/common-concerns/genital-warts', title: 'Genital Warts (HPV)' }
  };

  const getYouTubeId = (url) => {
    const match = url?.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?\s]+)/);
    return match ? match[1] : null;
  };

  return (
    <PageWrapper>
      <Breadcrumbs items={[
        { label: 'Education Library', path: '/education' },
        { label: 'Urologic Cancers', path: '/cancer' },
        { label: article.title.en, path: `/cancer/${article.id}` }
      ]} />

      <article className="article-full cancer-article" data-testid="cancer-article">
        {/* Header */}
        <header className="article-header cancer-header">
          <div className="cancer-ribbon">🎗️</div>
          <h1>{article.title[language]}</h1>
          <div className="article-meta">
            <span><Clock size={16} /> {readingTime} min read</span>
            <button 
              onClick={toggleLanguage}
              className="language-btn"
              data-testid="language-toggle"
            >
              <Globe size={16} />
              {language === 'en' ? 'Ver en Español' : 'View in English'}
            </button>
          </div>
        </header>

        {/* Article Image */}
        {article.image && (
          <div className="article-image-container" data-testid="article-image">
            <img 
              src={article.image.url} 
              alt={article.image.alt[language]}
              className="article-main-image"
            />
            {article.image.caption && (
              <figcaption className="article-image-caption">
                <span className="caption-text">{article.image.caption[language]}</span>
                <span className="caption-source">{article.image.source}</span>
              </figcaption>
            )}
          </div>
        )}

        {/* Key Points Summary */}
        {article.keyPoints && (
          <div className="key-points-box" data-testid="key-points">
            <div className="key-points-header">
              <CheckCircle2 size={20} />
              <h3>{language === 'en' ? 'Key Points' : 'Puntos Clave'}</h3>
            </div>
            <ul>
              {article.keyPoints[language].map((point, idx) => (
                <li key={idx}>{point}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Body */}
        <div className="article-body">
          {renderContent(article.content[language])}

          {/* Disclaimer */}
          <div className="info-callout" data-testid="disclaimer">
            <div className="info-callout-header">
              <AlertCircle size={18} />
              <h3>{language === 'en' ? 'Important Note' : 'Nota Importante'}</h3>
            </div>
            <p>
              {language === 'en' 
                ? 'This information is for educational purposes only. Every case is different. Please discuss your specific situation with your healthcare provider to determine the best approach for you.'
                : 'Esta información es solo para fines educativos. Cada caso es diferente. Por favor discuta su situación específica con su proveedor de atención médica.'}
            </p>
          </div>

          {/* Related Videos */}
          {relatedVideos.length > 0 && (
            <div className="related-videos-section" data-testid="related-videos">
              <h3>{language === 'en' ? 'Related Videos' : 'Videos Relacionados'}</h3>
              <div className="related-videos-grid">
                {relatedVideos.map(video => (
                  <a 
                    key={video.id}
                    href={video.youtube_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="related-video-card"
                  >
                    <div className="video-thumb">
                      <img 
                        src={`https://img.youtube.com/vi/${getYouTubeId(video.youtube_url)}/mqdefault.jpg`}
                        alt={video.title}
                      />
                      <div className="play-overlay">▶</div>
                    </div>
                    <div className="video-info">
                      <h4>{video.title}</h4>
                      <span className="video-source">{video.source}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Related Topics */}
          {article.relatedTopics && article.relatedTopics.length > 0 && (
            <div className="related-section">
              <h3>{language === 'en' ? 'Related Topics' : 'Temas Relacionados'}</h3>
              <div className="related-chips">
                {article.relatedTopics.map((topicId, idx) => {
                  const topic = topicPaths[topicId];
                  if (!topic) return null;
                  return (
                    <Link key={idx} to={topic.path} className="related-chip">
                      {topic.title}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </article>
    </PageWrapper>
  );
};

export default CancerArticle;
